/**
 * Разбор анкеты СберАвто (трейд-ин) из сообщения мессенджера.
 *
 * Бывает три вида сообщения:
 *  - первичная анкета «Подпись: значение» (при копировании из мессенджера
 *    строки часто склеиваются в одну): «📋 ПЕРВИЧНАЯ АНКЕТА TRADE-IN …»;
 *  - дополненная: перед анкетой приписка квалификации ФО СберАвто
 *    («ID 4494204 Связалась. Стоп Факторов нет. … ОСМОТР: <адрес>»);
 *  - короткая свободным текстом, без подписей («первый поток»):
 *    «ID 5000001 Chevrolet Aveo К000СХ77. Иван 8 900 … МЕСТО ОСМОТРА: <адрес>.
 *    2 ключа…» — разбирается эвристиками, диспетчер проверяет поля.
 *
 * Разбор идёт только в браузере, текст никуда не отправляется — в БД уходят
 * уже разобранные и проверенные диспетчером поля (RPC upsert_sberauto_lead).
 */

export interface SberAutoParsed {
  externalId?: string
  clientName?: string
  /** +7XXXXXXXXXX, если номер российский; иначе как в сообщении. */
  clientPhone?: string
  city?: string
  brand?: string
  model?: string
  vin?: string
  plate?: string
  newBrand?: string
  newModel?: string
  comment?: string
  inspectionAddress?: string
  qualificationNote?: string
}

const LABELS = [
  'ID клиента в CRM:',
  'ФИО клиента:',
  'Номер телефона:',
  'Город нахождения авто:',
  'Сдаваемый автомобиль (Trade-In):',
  'Выбранный новый автомобиль:',
  'Марка:',
  'Модель:',
  'ГРЗ / VIN:',
  'Комментарий:',
  'Способ приобретения:',
  'Дата создания:',
] as const

const VIN_RE = /^[A-HJ-NPR-Z0-9]{17}$/

// Латинские буквы, похожие на кириллицу в госномере (K510CX77 → К510СХ77).
const LAT2CYR: Record<string, string> = {
  A: 'А', B: 'В', E: 'Е', K: 'К', M: 'М', H: 'Н', O: 'О', P: 'Р', C: 'С', T: 'Т', Y: 'У', X: 'Х',
}
export function normalizePlate(s: string): string {
  return s.toUpperCase().replace(/\s/g, '').replace(/[ABEKMHOPCTYX]/g, (c) => LAT2CYR[c] ?? c)
}
const PL = 'ABEKMHOPCTYXАВЕКМНОРСТУХ'
const PLATE_RE = new RegExp(`(?<![0-9A-ZА-Я])([${PL}]\\d{3}[${PL}]{2}\\d{2,3})(?![0-9])`, 'i')
const PHONE_RE = /(?:\+7|\b8|\b7)[\s\-()]*\d{3}[\s\-()]*\d{3}[\s\-]*\d{2}[\s\-]*\d{2}(?!\d)/
const VIN_FREE_RE = /\b(?=[A-HJ-NPR-Z0-9]*[A-HJ-NPR-Z])[A-HJ-NPR-Z0-9]{17}\b/i
const ADDRESS_RE = /(?:МЕСТО\s+ОСМОТРА|ОСМОТР)\s*:\s*/i

function clean(v: string): string {
  return v
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    // Разделитель «-----» или «-—————» (дефис, длинное/короткое тире).
    .replace(/[-–—―]{3,}/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/** «jetour» → «Jetour», «great wall» → «Great Wall». Окончательное
 *  написание марки уточняется по справочнику в диалоге. */
export function titleBrand(s: string): string {
  return s.trim().toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_m, a: string, b: string) => a + b.toUpperCase())
}

/** Заглавными только латинские слова: «tiggo 7 pro» → «Tiggo 7 Pro»,
 *  «T9 Ультра 5 мест» без изменений. */
export function titleModel(s: string): string {
  return s.trim().replace(/(^|\s)([a-z])/g, (_m, a: string, b: string) => a + b.toUpperCase())
}

function stripBrand(model?: string, brand?: string): string | undefined {
  if (!model || !brand) return model
  const b = brand.toLowerCase()
  const m = model.toLowerCase()
  if (m.startsWith(b + ' ')) {
    const rest = model.slice(brand.length).trim()
    if (rest) return rest
  }
  return model
}

export function normalizeRuPhone(raw: string): string {
  let d = raw.replace(/\D/g, '')
  if (/^8\d{10}$/.test(d)) d = '7' + d.slice(1)
  if (/^9\d{9}$/.test(d)) d = '7' + d
  return /^7\d{10}$/.test(d) ? '+' + d : raw.trim()
}

export function parseSberAutoMessage(text: string): SberAutoParsed {
  const t = (text || '').replace(/\s+/g, ' ')
  const hits: { lab: string; at: number }[] = []
  for (const lab of LABELS) {
    let i = t.indexOf(lab)
    while (i >= 0) {
      hits.push({ lab, at: i })
      i = t.indexOf(lab, i + lab.length)
    }
  }
  hits.sort((a, b) => a.at - b.at)

  const r: SberAutoParsed = {}
  let section: 'old' | 'new' | '' = ''
  hits.forEach((h, i) => {
    const end = i + 1 < hits.length ? hits[i + 1].at : t.length
    const v = clean(t.slice(h.at + h.lab.length, end))
    switch (h.lab) {
      case 'ID клиента в CRM:':
        if (v) r.externalId = v.replace(/\s/g, '')
        break
      case 'ФИО клиента:':
        if (v) r.clientName = v
        break
      case 'Номер телефона:':
        if (v) r.clientPhone = normalizeRuPhone(v)
        break
      case 'Город нахождения авто:':
        if (v) r.city = v
        break
      case 'Сдаваемый автомобиль (Trade-In):':
        section = 'old'
        break
      case 'Выбранный новый автомобиль:':
        section = 'new'
        break
      case 'Марка:':
        if (!v) break
        if (section === 'new') r.newBrand = titleBrand(v)
        else r.brand = titleBrand(v)
        break
      case 'Модель:':
        if (!v) break
        if (section === 'new') r.newModel = titleModel(v)
        else r.model = titleModel(v)
        break
      case 'ГРЗ / VIN:': {
        const u = v.toUpperCase().replace(/\s/g, '')
        if (!u) break
        if (VIN_RE.test(u)) r.vin = u
        else r.plate = normalizePlate(u)
        break
      }
      case 'Комментарий:':
        if (v) r.comment = v
        break
    }
  })

  // Без подписей полей — короткое сообщение свободным текстом.
  if (hits.length === 0) return parseFreeform(t)

  // «Марка: JAECOO Модель: JAECOO J8 …» — марку из начала модели убираем.
  r.model = stripBrand(r.model, r.brand)
  r.newModel = stripBrand(r.newModel, r.newBrand)

  // Приписка перед анкетой: квалификация ФО СберАвто и адрес осмотра.
  const firstAt = hits[0].at
  let pre = clean(
    t.slice(0, firstAt).replace(/ПЕРВИЧНАЯ АНКЕТА[^(]*\(СБЕРАВТО\)/i, ''),
  )
  if (pre) {
    const addr = pre.match(/(?:МЕСТО\s+ОСМОТРА|ОСМОТР)\s*:\s*(.+)$/i)
    if (addr) {
      r.inspectionAddress = addr[1].trim()
      pre = pre.slice(0, addr.index).trim()
    }
    pre = pre.replace(/^ID\s*\d+\s*/i, '').trim()
    if (pre) r.qualificationNote = pre
  }
  return r
}

/**
 * Короткое сообщение без подписей: «ID <номер> <марка модель> <госномер>.
 * <имя> <телефон>. МЕСТО ОСМОТРА: <адрес>. <квалификация>». Порядок частей
 * может отличаться — каждую ищем своим шаблоном, остаток уходит в квалификацию.
 */
function parseFreeform(t: string): SberAutoParsed {
  const r: SberAutoParsed = {}
  const text = clean(t)
  if (!text) return r

  const id = text.match(/^ID\s*(\d{4,})\b/i)
  if (id) r.externalId = id[1]
  const afterId = id ? id[0].length : 0

  const phone = text.match(PHONE_RE)
  if (phone) r.clientPhone = normalizeRuPhone(phone[0])

  const vin = text.match(VIN_FREE_RE)
  if (vin) r.vin = vin[0].toUpperCase()
  const plate = vin ? null : text.match(PLATE_RE)
  if (plate) r.plate = normalizePlate(plate[1])
  const carCode = vin ?? plate

  // Адрес — от «МЕСТО ОСМОТРА:» до конца предложения; всё после — квалификация.
  let headEnd = text.length
  const addr = ADDRESS_RE.exec(text)
  if (addr) {
    headEnd = addr.index
    const rest = text.slice(addr.index + addr[0].length)
    const stop = rest.search(/\.(\s|$)/)
    r.inspectionAddress = (stop >= 0 ? rest.slice(0, stop) : rest).trim()
    const note = stop >= 0 ? rest.slice(stop + 1).trim() : ''
    if (note) r.qualificationNote = note
  }

  // Марка и модель — между номером CRM и госномером/VIN.
  if (carCode?.index !== undefined && carCode.index > afterId) {
    const car = text.slice(afterId, carCode.index).replace(/[.,;:]/g, ' ').trim()
    const words = car.split(/\s+/).filter(Boolean)
    if (words.length >= 1 && words.length <= 5) {
      r.brand = titleBrand(words[0])
      if (words.length > 1) r.model = titleModel(words.slice(1).join(' '))
    }
  }

  // Имя — слова между госномером/VIN (или номером CRM) и телефоном.
  if (phone?.index !== undefined) {
    const from = carCode?.index !== undefined ? carCode.index + carCode[0].length : afterId
    if (phone.index > from) {
      const name = text.slice(from, phone.index).replace(/[.,;:]/g, ' ').replace(/\s+/g, ' ').trim()
      if (name && /^[\p{L}\s-]+$/u.test(name) && name.split(' ').length <= 3) r.clientName = name
    }
  }

  // Квалификация без адреса: всё, что после телефона, если там что-то есть.
  if (!addr && phone?.index !== undefined) {
    const tail = text.slice(phone.index + phone[0].length).replace(/^[.,;:\s]+/, '').trim()
    if (tail) r.qualificationNote = tail
  }
  if (!r.qualificationNote && headEnd === text.length && !phone && !carCode) {
    r.qualificationNote = text
  }
  return r
}

/** Сколько полей удалось вытащить — для подсказки в диалоге. */
export function countParsed(r: SberAutoParsed): number {
  return Object.values(r).filter((v) => v !== undefined && v !== '').length
}
